import { Fragment } from "react";

interface Props {
  stages: string[];
  activeStage: number;
  currentStage: number;
  onStageClick: (index: number) => void;
}

// xl and up connector lines
const CONNECTOR_TRACK_CLASSES =
  "hidden shrink-0 items-center justify-center px-0.5 sm:flex md:grow xl:self-start xl:px-2.5 2xl:px-3";
const CONNECTOR_LINE_CLASSES =
  "w-px h-7 rotate-[20deg] bg-border xl:mt-[13px] xl:h-0.5 xl:w-8 xl:rotate-0 2xl:w-12";

const INTERACTIVE_DOT_CLASSES =
  "max-xl:group-hover:bg-brand-teal/50 max-xl:group-focus-visible:bg-brand-teal/50 xl:group-hover:border-brand-teal/40 xl:group-focus-visible:border-brand-teal/40 xl:group-active:border-brand-teal/70";
const INTERACTIVE_LABEL_CLASSES =
  "group-hover:text-brand-teal group-focus-visible:text-brand-teal group-active:text-brand-teal";
const FOCUS_RING_CLASSES =
  "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

const StepArrow = ({ flip }: { flip?: boolean }) => (
  <svg
    viewBox="0 0 28 12"
    className={`w-7 ${flip ? "rotate-180" : ""}`}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M.7 6h26.6" />
    <path d="m21.6 1.45 5.7 4.55-5.7 4.55" />
  </svg>
);

const WorkflowStepper = ({ stages, activeStage, currentStage, onStageClick }: Props) => {
  // Users may only open steps they have already reached — completed steps plus the active one.
  const lastNavigableStage = Math.min(currentStage, stages.length - 1);
  const canGoBack = activeStage > 0;
  const canGoForward = activeStage < lastNavigableStage;
  const arrowClasses =
    `hidden xl:block text-muted-foreground transition-colors hover:text-brand-teal focus-visible:text-brand-teal active:text-brand-teal disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-muted-foreground h-full p-2 lg:mx-4 ${FOCUS_RING_CLASSES}`;

  return (
    <nav
      aria-label="Workflow steps"
      className="flex w-full items-start justify-center gap-y-2 md:items-center lg:w-auto lg:gap-x-1 xl:gap-x-0"
    >
      <button
        type="button"
        aria-label="Previous step"
        onClick={() => onStageClick(activeStage - 1)}
        disabled={!canGoBack}
        className={arrowClasses}
      >
        <StepArrow flip />
      </button>

      {stages.map((stage, i) => {
        const isCurrent = i === activeStage;
        const isClickable = i <= lastNavigableStage;
        // Only a reachable step that isn't already open reacts to hover/press.
        const isInteractive = isClickable && !isCurrent;

        return (
          <Fragment key={stage}>
            <button
              type="button"
              onClick={() => onStageClick(i)}
              disabled={!isClickable}
              aria-current={isCurrent ? "step" : undefined}
              className={`flex min-w-0 flex-1 flex-col items-center md:items-baseline xl:items-center gap-1 px-0.5 md:flex-none md:flex-row md:justify-center md:gap-2 md:px-2 xl:flex-col xl:gap-1.5 ${FOCUS_RING_CLASSES} ${isInteractive ? "group" : ""} ${isClickable ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}
            >
              <span
                className={`w-2.5 h-2.5 rounded-full shrink-0 transition-colors md:translate-y-px xl:h-7 xl:w-7 xl:translate-y-0 xl:border-[7px] ${isCurrent ? "bg-brand-teal xl:border-brand-teal xl:bg-background" : "bg-muted-foreground/30 xl:border-muted-foreground/20 xl:bg-muted-foreground/10"} ${isInteractive ? INTERACTIVE_DOT_CLASSES : ""}`}
              />
              <span className="grid text-xs leading-tight text-center w-min md:w-auto md:whitespace-nowrap lg:w-min xl:w-auto xl:whitespace-nowrap xl:text-sm">
                {/* Bold is wider than medium, so every label reserves its bold width — otherwise
                    activating a step would widen its column and shift the whole row. */}
                <span aria-hidden="true" className="col-start-1 row-start-1 invisible font-bold">
                  {stage}
                </span>
                <span
                  className={`col-start-1 row-start-1 transition-colors ${isCurrent ? "font-bold text-brand-teal" : "font-medium text-muted-foreground"} ${isInteractive ? INTERACTIVE_LABEL_CLASSES : ""}`}
                >
                  {stage}
                </span>
              </span>
            </button>
            {i < stages.length - 1 && (
              <span aria-hidden="true" className={CONNECTOR_TRACK_CLASSES}>
                <span className={CONNECTOR_LINE_CLASSES} />
              </span>
            )}
          </Fragment>
        );
      })}

      <button
        type="button"
        aria-label="Next step"
        onClick={() => onStageClick(activeStage + 1)}
        disabled={!canGoForward}
        className={arrowClasses}
      >
        <StepArrow />
      </button>
    </nav>
  );
};

export default WorkflowStepper;
